import { ActiveLink } from '@lark-apaas/client-toolkit-lite';
import { Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import InstallGuideProvider from '@/components/install-guide/InstallGuideProvider';
import { Home, Dumbbell, Utensils, ClipboardList, User } from 'lucide-react';

const TAB_ITEMS = [
  { path: '/', label: '首页', Icon: Home },
  { path: '/training', label: '训练', Icon: Dumbbell },
  { path: '/diet', label: '饮食', Icon: Utensils },
  { path: '/custom', label: '定制', Icon: ClipboardList },
  { path: '/profile', label: '我的', Icon: User },
];

// 全屏沉浸页面（跟练页/训练日详情/问卷页/订阅页/报告页等）不显示底部 Tab
// 仅按路径前缀判断，不看任何状态——训练中也能切 Tab，避免拦截导致的"Tab 点不动"
// 判断标准：有返回按钮的二级操作/详情/执行/支付页 → 隐藏；Tab 主导航下的一级列表页 → 保留
const HIDE_TABBAR_PATHS = [
  '/login',
  // 训练执行 / 倒计时 / 休息
  '/workout',
  '/rest',

  // 测评 / 问卷
  '/simple-assessment',
  '/detailed-assessment',
  '/questionnaire',
  '/diet-assessment',

  // 订阅 / 支付 / 会员
  '/subscribe',
  '/membership',

  // 报告 / 周期报告
  '/cycle-report',
  '/weekly-report',

  // 晋升突破合集（详情/跟练）
  '/fanjue',

  // 伤病恢复方案页
  '/recovery-workout',

  // 详情页（带返回键的二级页）
  '/day/',           // 训练日详情页（动作列表）
  '/course/',        // 课程详情页
  '/collection/',    // 合集详情页
  '/exercise/',      // 动作详情页

  // 自定义计划编辑页（"1副本"这类有取消/保存修改按钮的页面）
  '/custom-plans/',  // 自定义计划详情/编辑页
  '/plan-picker/',   // 计划动作选择器页

  // 动作库（有返回键的二级页，底部按钮会遮挡「智能编组/保存计划」）
  '/exercise-library',

  // 睡眠/心情/伤病信号 等详情页
  '/sleep-plan',
  '/mood-tips',
  '/body-signals',

  // 设置页（二级）
  '/settings',
];

export function Layout() {
  const location = useLocation();

  // Tab 切换时滚动到顶部：每次路径变化都重置滚动位置
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);

  const shouldHideTabbar = HIDE_TABBAR_PATHS.some(p =>
    location.pathname.startsWith(p)
  );

  return (
    <InstallGuideProvider><div className="app-bg min-h-screen w-full">
      {/* 手机视口容器 - 居中显示 */}
      <div className="mx-auto w-full max-w-[420px] relative">
        <main className={`min-h-screen ${shouldHideTabbar ? '' : 'pb-tabbar'}`}>
          <Outlet />
        </main>

        {/* 底部 Tab 导航 */}
        {!shouldHideTabbar && (
          <nav
            className="fixed bottom-0 left-1/2 z-50 -translate-x-1/2 w-full max-w-[420px]"
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
          >
            <div className="mx-3 mb-2 rounded-2xl metallic-card brushed-metal px-2 py-2 flex items-center justify-around">
              {TAB_ITEMS.map(item => {
                const Icon = item.Icon;
                return (
                  <ActiveLink
                    key={item.path}
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all relative ${
                        isActive
                          ? 'text-primary'
                          : 'text-muted-foreground'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          className={`w-5 h-5 ${
                            isActive ? 'stroke-[2.5]' : 'stroke-[2]'
                          }`}
                        />
                        <span className={`text-[10px] ${isActive ? 'font-semibold' : 'font-medium'}`}>
                          {item.label}
                        </span>
                        {isActive && (
                          <span className="w-1 h-1 rounded-full bg-primary absolute -bottom-0.5" />
                        )}
                      </>
                    )}
                  </ActiveLink>
                );
              })}
            </div>
          </nav>
        )}

      </div>
    </div></InstallGuideProvider>
  );
}
